import { store } from '../store/appStore';
import { previewStore, hashSliceInput } from './previewStore';
import type { SliceRequest, SliceWorkerMsg } from '../slice/types';

let worker: Worker | null = null;
let nextId = 1;
const cache = new Map<string, Extract<SliceWorkerMsg, { type: 'done' }>>();

function getWorker(): Worker {
  if (!worker) {
    worker = new Worker(new URL('../slice/slicer.worker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (ev: MessageEvent<SliceWorkerMsg>) => {
      const msg = ev.data;
      const cur = previewStore.getSnapshot();
      if (msg.requestId !== cur.requestId) return;
      if (msg.type === 'progress') {
        if (msg.layer) previewStore.appendLayer(msg.layer, msg.pct, msg.message);
        else previewStore.set({ status: 'slicing', progress: msg.pct, message: msg.message });
      } else if (msg.type === 'done') {
        cache.set(cur.hash, msg);
        if (cache.size > 6) {
          const first = cache.keys().next().value;
          if (first) cache.delete(first);
        }
        const last = Math.max(0, msg.layers.length - 1);
        previewStore.set({
          status: 'ready',
          progress: 100,
          message: 'Preview ready',
          layers: msg.layers,
          currentLayer: last,
          layerDraw: 1,
          estimate: msg.estimate,
          elapsedMs: msg.elapsedMs,
          triangleCount: msg.triangleCount,
          segmentCount: msg.segmentCount,
        });
      } else if (msg.type === 'error') {
        previewStore.set({ status: 'error', error: msg.message, message: msg.message, playing: false });
      }
    };
    worker.onerror = (e) => {
      previewStore.set({ status: 'error', error: e.message || 'Worker error', message: e.message || 'Worker error' });
    };
  }
  return worker;
}

if (typeof window !== 'undefined') {
  (window as unknown as { __layerforgePreview?: typeof previewStore }).__layerforgePreview = previewStore;
}

export function syncPreviewSlice() {
  const app = store.getSnapshot();
  if (app.viewMode !== 'preview') {
    previewStore.set({ playing: false });
    return;
  }
  if (!app.objects.length) {
    previewStore.set({
      status: 'idle',
      layers: [],
      message: 'Upload a model to preview',
      progress: 0,
      estimate: null,
    });
    return;
  }
  const hash = hashSliceInput(app.objects, app.settings);
  const snap = previewStore.getSnapshot();
  if ((snap.status === 'ready' || snap.status === 'slicing') && snap.hash === hash) return;
  const cached = cache.get(hash);
  if (cached) {
    previewStore.set({
      status: 'ready',
      progress: 100,
      message: 'Preview ready',
      layers: cached.layers,
      currentLayer: Math.max(0, cached.layers.length - 1),
      layerDraw: 1,
      estimate: cached.estimate,
      elapsedMs: cached.elapsedMs,
      triangleCount: cached.triangleCount,
      segmentCount: cached.segmentCount,
      hash,
      error: null,
      requestId: cached.requestId,
      playing: false,
    });
    return;
  }
  const requestId = nextId++;
  previewStore.resetForSlice(hash, requestId);
  const req: SliceRequest = {
    requestId,
    settings: { ...app.settings },
    objects: app.objects.map((o) => ({
      id: o.id,
      positions: o.positions,
      position: o.position,
      rotation: o.rotation,
      scale: o.scale,
      overrides: o.overrides,
      paintSupports: o.paintSupports,
    })),
  };
  getWorker().postMessage(req);
}

export function cancelPreview() {
  nextId++;
}
